import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Mail, ArrowRight, Loader2, Search, LogOut } from "lucide-react";
import { externalSupabase } from "@/lib/externalSupabase";
import DashboardPropertyCard, {
  type DashboardProperty,
} from "@/components/DashboardPropertyCard";
import DashboardProfile, { type GuestProfile } from "@/components/DashboardProfile";
import GallerySync from "@/components/GallerySync";
import type { Session } from "@supabase/supabase-js";

const Dashboard = () => {
  const [email, setEmail] = useState("");
  const [session, setSession] = useState<Session | null>(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [properties, setProperties] = useState<DashboardProperty[]>([]);
  const [fetching, setFetching] = useState(false);
  const [profile, setProfile] = useState<GuestProfile | null>(null);
  const [debugMessage, setDebugMessage] = useState<string | null>(null);

  // Listen for auth state changes
  useEffect(() => {
    const {
      data: { subscription },
    } = externalSupabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setCheckingSession(false);
    });

    externalSupabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setCheckingSession(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fetch data when session is available
  useEffect(() => {
    if (session) {
      fetchAllSearchRequests();
      if (session.user?.email) fetchProfile(session.user.email);
    }
  }, [session]);

  const fetchProfile = async (userEmail: string) => {
    const { data, error } = await externalSupabase
      .from("guests")
      .select("id, email, full_name, phone_number, airbnb_profile_url, avatar_url, subscription_tier")
      .eq("email", userEmail.toLowerCase())
      .maybeSingle();

    console.log("[Dashboard] fetchProfile result:", { avatar_url: data?.avatar_url, error });

    if (data) {
      setProfile(data as GuestProfile);
    }
  };

  const fetchAllSearchRequests = async () => {
    setFetching(true);
    const userEmail = session?.user?.email?.toLowerCase() || "unknown";
    try {
      // Try FK join first, then fall back to URL matching
      const { data: rows, error: fetchErr } = await (externalSupabase
        .from("search_requests") as any)
        .select("*, property:properties!search_requests_property_id_fkey(masked_title, original_name, image_url, is_verified, owner_website, city, country, bedrooms, property_type, airbnb_url)")
        .ilike("guest_email", userEmail)
        .order("created_at", { ascending: false });

      console.log("[Dashboard] rows with join:", JSON.stringify(rows?.slice(0, 2), null, 2));

      if (fetchErr) {
        console.error("[Dashboard] fetch error:", fetchErr);
        setError(`Database error: ${fetchErr.message}`);
        setFetching(false);
        return;
      }

      if (!rows || rows.length === 0) {
        setProperties([]);
        setDebugMessage(`DATABASE CONNECTED BUT 0 ROWS FOUND FOR ${userEmail}`);
        setFetching(false);
        return;
      }

      // Collect rows missing property data for URL-based fallback
      const missingRows = rows.filter((r: any) => !r.property && r.listing_url);
      let propertiesMap: Record<string, any> = {};

      if (missingRows.length > 0) {
        const urls = [...new Set(missingRows.map((r: any) => r.listing_url))] as string[];
        console.log("[Dashboard] Fallback URL lookup for", urls.length, "URLs, sample:", urls[0]);
        
        const { data: props } = await (externalSupabase
          .from("properties") as any)
          .select("airbnb_url, masked_title, original_name, image_url, is_verified, owner_website, city, country, bedrooms, property_type")
          .in("airbnb_url", urls);

        console.log("[Dashboard] URL fallback results:", props?.length, "matches");

        if (props) {
          for (const p of props) {
            propertiesMap[p.airbnb_url] = p;
          }
        }
      }

      setDebugMessage(null);

      const mapped: DashboardProperty[] = rows.map((r: any) => {
        const prop = r.property || (r.listing_url ? propertiesMap[r.listing_url] : null);
        return {
          id: r.property_id || r.id,
          listing_url: r.listing_url || prop?.airbnb_url || null,
          status: r.status,
          created_at: r.created_at,
          property_name: prop?.masked_title || prop?.original_name || null,
          owner_website: prop?.owner_website || null,
          image_url: prop?.image_url || null,
          city: prop?.city || null,
          country: prop?.country || null,
          is_verified: prop?.is_verified || false,
          bedrooms: prop?.bedrooms || null,
          property_type: prop?.property_type || null,
        };
      });

      setProperties(mapped);
    } catch (err: any) {
      setError("Failed to load searches.");
      console.error("[Dashboard] catch:", err);
    } finally {
      setFetching(false);
    }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("Please enter a valid email address.");
      return;
    }

    setError(null);
    setLoading(true);

    const { error: authError } = await externalSupabase.auth.signInWithOtp({
      email: trimmed,
      options: {
        emailRedirectTo: window.location.origin + "/auth/callback",
      },
    });

    setLoading(false);

    if (authError) {
      setError(authError.message);
      return;
    }

    setMagicLinkSent(true);
  };

  const handleSignOut = async () => {
    await externalSupabase.auth.signOut();
    setSession(null);
    setProperties([]);
    setMagicLinkSent(false);
  };

  if (checkingSession) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 pt-24 pb-16">
        <div className="container mx-auto px-6 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-10"
          >
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-3">
              Your Dashboard
            </h1>
            <p className="text-muted-foreground">
              View all properties you've searched for and their direct booking
              status.
            </p>
            {session && (
              <button
                onClick={handleSignOut}
                className="mt-3 text-xs text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
              >
                <LogOut className="w-3 h-3" />
                Sign out ({session.user.email})
              </button>
            )}
          </motion.div>

          {!session ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="max-w-md mx-auto"
            >
              <div className="rounded-2xl border bg-card p-8 space-y-5">
                {magicLinkSent ? (
                  <div className="text-center space-y-3">
                    <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center mx-auto">
                      <Mail className="w-5 h-5 text-accent-foreground" />
                    </div>
                    <h2 className="font-display font-semibold text-foreground">
                      Check your email
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      We sent a magic link to{" "}
                      <span className="font-medium text-foreground">
                        {email}
                      </span>
                      . Click the link to sign in.
                    </p>
                    <button
                      onClick={() => setMagicLinkSent(false)}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Use a different email
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleMagicLink} className="space-y-5">
                    <div className="text-center">
                      <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center mx-auto mb-3">
                        <Search className="w-5 h-5 text-accent-foreground" />
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Sign in with your email to view your searches
                      </p>
                    </div>

                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@email.com"
                        autoFocus
                        className="w-full pl-10 pr-4 py-3 rounded-xl border bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-shadow"
                      />
                    </div>

                    {error && (
                      <p className="text-sm text-destructive">{error}</p>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gradient-hero text-primary-foreground py-3 rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity shadow-savings disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Magic Link
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          ) : (
            <>
              <GallerySync userEmail={session.user.email} />
              {profile && (
                <DashboardProfile
                  profile={profile}
                  onProfileUpdate={(updated) => setProfile(updated)}
                />
              )}
              {fetching ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <AnimatePresence>
                {properties.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-16"
                >
                  {debugMessage && (
                    <p className="text-destructive text-3xl font-black mb-4 border-2 border-destructive p-6 rounded-xl">
                      {debugMessage}
                    </p>
                  )}
                  <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                    <Search className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground mb-4">
                    No searches yet.
                  </p>
                  <a
                    href="/search"
                    className="text-sm font-semibold text-primary hover:underline"
                  >
                    Search for your first property →
                  </a>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
                >
                  {properties.map((prop, i) => (
                    <DashboardPropertyCard key={prop.id} property={prop} index={i} />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Dashboard;
